(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ama-data-orbit]';
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.dataset.amaOrbitReady === 'true') return;
    root.dataset.amaOrbitReady = 'true';

    const numberEl = root.querySelector('[data-ama-orbit-number]');
    const categoryEl = root.querySelector('[data-ama-orbit-category]');
    const copyEl = root.querySelector('[data-ama-orbit-copy]');
    const sourceEl = root.querySelector('[data-ama-orbit-source]');
    const buttons = Array.from(root.querySelectorAll('.ama-data-orbit__pager-btn'));
    const pointerZone = root.querySelector('[data-ama-orbit-pointer]');
    const canvas = root.querySelector('[data-ama-orbit-canvas]');

    if (!numberEl || !categoryEl || !copyEl || !sourceEl || !buttons.length) return;

    let activeIndex = 0;
    let timer = null;
    let visible = false;
    let canvasRunning = false;

    function setStat(index, fromUser) {
      const button = buttons[index];
      if (!button) return;

      activeIndex = index;
      root.dataset.state = String(index);

      buttons.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const nextNumber = button.dataset.number || '';
      categoryEl.textContent = button.dataset.category || '';
      copyEl.textContent = button.dataset.copy || '';
      sourceEl.textContent = button.dataset.source || '';
      if (button.dataset.sourceUrl) sourceEl.href = button.dataset.sourceUrl;

      if (reduceMotion) {
        numberEl.textContent = nextNumber;
      } else {
        scramble(numberEl, nextNumber);
        copyEl.animate(
          [
            { opacity: .22, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          { duration: 420, easing: 'cubic-bezier(.2,.75,.2,1)' }
        );
      }

      if (fromUser) restartTimer();
    }

    function scramble(el, finalText) {
      const glyphs = '0123456789%×/';
      const previous = el.textContent || '';
      const started = performance.now();
      const duration = 470;

      function frame(now) {
        const progress = Math.min(1, (now - started) / duration);

        if (progress < .72) {
          const length = Math.max(previous.length, finalText.length);
          let out = '';
          for (let i = 0; i < length; i += 1) {
            const target = finalText[i] || ' ';
            out += /[0-9%×/]/.test(target)
              ? glyphs[Math.floor(Math.random() * glyphs.length)]
              : target;
          }
          el.textContent = out;
        } else {
          el.textContent = finalText;
        }

        if (progress < 1) requestAnimationFrame(frame);
      }

      requestAnimationFrame(frame);
    }

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      if (reduceMotion || !visible || timer) return;
      timer = window.setInterval(() => {
        setStat((activeIndex + 1) % buttons.length, false);
      }, 5600);
    }

    function restartTimer() {
      stopTimer();
      startTimer();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => setStat(index, true));
    });

    if (pointerZone && !reduceMotion) {
      pointerZone.addEventListener('pointermove', (event) => {
        const rect = pointerZone.getBoundingClientRect();
        pointerZone.style.setProperty('--ado-pointer-x', ((event.clientX - rect.left) / rect.width * 100) + '%');
        pointerZone.style.setProperty('--ado-pointer-y', ((event.clientY - rect.top) / rect.height * 100) + '%');
      }, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          visible = entry.isIntersecting && entry.intersectionRatio > .22;
          if (visible) {
            startTimer();
            canvasRunning = true;
          } else {
            stopTimer();
            canvasRunning = false;
          }
        });
      }, { threshold: [0, .22, .5] });
      observer.observe(root);
    } else {
      visible = true;
      canvasRunning = true;
      startTimer();
    }

    if (canvas && !reduceMotion) {
      const ctx = canvas.getContext('2d', { alpha: true });
      let particles = [];
      let width = 0;
      let height = 0;
      let dpr = 1;

      function resizeCanvas() {
        const rect = canvas.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
        dpr = Math.min(1.5, window.devicePixelRatio || 1);
        canvas.width = Math.max(1, Math.round(width * dpr));
        canvas.height = Math.max(1, Math.round(height * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const count = Math.max(34, Math.min(64, Math.floor(width * height / 15500)));
        particles = Array.from({ length: count }, (_, index) => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - .5) * .11,
          vy: (Math.random() - .5) * .11,
          r: Math.random() * 1.3 + .35,
          alpha: Math.random() * .32 + .10,
          warm: index % 10 === 0
        }));
      }

      function draw() {
        if (canvasRunning) {
          ctx.clearRect(0, 0, width, height);

          particles.forEach((particle) => {
            particle.x += particle.vx;
            particle.y += particle.vy;

            if (particle.x < 0) particle.x = width;
            if (particle.x > width) particle.x = 0;
            if (particle.y < 0) particle.y = height;
            if (particle.y > height) particle.y = 0;

            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
            ctx.fillStyle = particle.warm
              ? 'rgba(254,75,9,' + particle.alpha + ')'
              : 'rgba(0,76,150,' + particle.alpha + ')';
            ctx.fill();
          });

          for (let i = 0; i < particles.length; i += 1) {
            for (let j = i + 1; j < particles.length; j += 1) {
              const a = particles[i];
              const b = particles[j];
              const dx = a.x - b.x;
              const dy = a.y - b.y;
              const distance = Math.hypot(dx, dy);

              if (distance < 84) {
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
                ctx.strokeStyle = (a.warm || b.warm)
                  ? 'rgba(254,75,9,' + (.032 * (1 - distance / 84)) + ')'
                  : 'rgba(0,76,150,' + (.048 * (1 - distance / 84)) + ')';
                ctx.lineWidth = .65;
                ctx.stroke();
              }
            }
          }
        }

        requestAnimationFrame(draw);
      }

      resizeCanvas();
      draw();
      window.addEventListener('resize', resizeCanvas, { passive: true });
    }

    setStat(0, false);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
